import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import type { PropsWithClassName } from "@/components/podkit/lib/cn";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { createRunnerSetupURL } from "@/components/runners/RunnerSetupURL";
import { useRunnerAccessToken } from "@/queries/runner-queries";
import type { PlainMessage } from "@bufbuild/protobuf";
import { RunnerVariant, type Runner } from "gitpod-next-api/gitpod/v1/runner_pb";
import { useMemo, type FC } from "react";

export const CloudFormationStackCreateButton: FC<{
    runner: PlainMessage<Runner>;
    text: string;
    onClicked?: () => void;
} & PropsWithClassName> = ({ runner, text, onClicked, className }) => {
    const {
        data: tokenData,
        error,
        isLoading,
    } = useRunnerAccessToken(runner.runnerId, {
        enabled: !!runner.spec?.configuration?.region,
    });

    // Memoize the setup URL once we have the access token
    const setupURL = useMemo(() => {
        if (!tokenData?.accessToken || !runner.spec?.configuration?.region) {
            return null;
        }

        return createRunnerSetupURL(
            runner,
            tokenData.accessToken,
            runner.spec.configuration.region,
            `${window.location.origin}/api`,
            runner.spec?.variant === RunnerVariant.ENTERPRISE ? "enterprise" : "standard",
        );
    }, [runner, tokenData?.accessToken]);

    // Handle click event if provided
    const handleClick = () => {
        if (onClicked) {
            onClicked();
        }
    };

    const isDisabled = isLoading || !setupURL;

    return (
        <>
            <Button asChild className={className}>
                <ExternalLink
                    href={setupURL ?? "#"}
                    onClick={handleClick}
                    className="text-content-invert max-w-fit"
                    iconSize="sm"
                    disabled={isDisabled}
                    data-tracking-id="create-cloudformation-stack"
                >
                    {text}
                </ExternalLink>
            </Button>
            <ErrorMessage error={error} />
        </>
    );
};
